import LoadingSpinner from '@/components/LoadingSpinner';
import { demoModeAtom, reportDemoModeAtom } from '@/global-store';
import { useFetchReportDataForAllDomains } from '@/http/queries';
import ProductCards from '@components/ProductCards';
import UserTour from '@components/UserTour.jsx';
import { useAtom } from 'jotai';
import { Fragment, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import HomeReportTable, { type ReportRow } from './components/HomeReportTable';
import TableHeading from '@/components/Tables/TableHeading';
import { Typography, type TableRef } from '@eye-able/ui';
import HomeApplicationsBanner from './components/HomeApplicationsBanner';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';

const Home = () => {
  const { t } = useInlineTranslation();

  const [demoMode] = useAtom(demoModeAtom);
  const [reportDemoMode] = useAtom(reportDemoModeAtom);

  const refHomeReportTable = useRef<TableRef<ReportRow>>(null);

  const { AllReportsData, isLoading, isError } =
    useFetchReportDataForAllDomains();

  const isReportsData = Boolean(
    !reportDemoMode &&
    !isLoading &&
    !isError &&
    AllReportsData &&
    Object.keys(AllReportsData).length,
  );

  if (demoMode) {
    return <Navigate replace={false} to='/demo' />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Fragment>
      <PageTitle
        heading={<T k='homeGreeting' />}
        description={isReportsData ? undefined : <T k='dashboardText1' />}
      />

      <div className='eye:flex eye:flex-col eye:gap-l'>
        <section
          aria-label={t('allApplications')}
          className='eye:flex eye:flex-col eye:gap-xs'
        >
          <Typography tag='h2' variant='headline-s'>
            <T k='allApplications' />
          </Typography>
          <HomeApplicationsBanner />
        </section>

        {isReportsData ? (
          <section
            id='homeReportTableSection'
            aria-label={t('overviewOfAllDomains')}
          >
            <TableHeading
              title={<T k='overviewOfAllDomains' />}
              tableRef={refHomeReportTable}
            />
            <HomeReportTable
              ref={refHomeReportTable}
              data={Object.values(AllReportsData ?? {})}
            />
          </section>
        ) : (
          <section
            id='homeStartUsingSection'
            aria-label={t('startUsingService')}
            className='eye:flex eye:flex-col eye:gap-xs'
          >
            <Typography tag='h2' variant='headline-s'>
              <T k='startUsingService' />
            </Typography>
            <ProductCards />
          </section>
        )}
      </div>

      <UserTour />
    </Fragment>
  );
};

export default Home;
